import React, { useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { Eye, EyeOff, Plus, Copy, BadgeCheck, ShieldAlert, FlaskConical } from "lucide-react";
import { toast } from "sonner";

export default function BalanceCard({ wallet, onAddMoney }) {
  const { t, lang, features } = useApp();
  const testMode = features?.payments_mode === "test";
  const [hidden, setHidden] = useState(false);

  const copyWallet = () => {
    navigator.clipboard.writeText(wallet?.wallet_number || "");
    toast.success(t.copied);
  };

  return (
    <motion.div
      data-testid="wallet-balance-card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-900 border border-emerald-500/30 shadow-xl shadow-emerald-950/40 text-white"
    >
      <div className="absolute inset-0 card-shine pointer-events-none" />
      <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-emerald-400/10 blur-2xl" />

      <div className="relative flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-white/60">
          {t.balance}
          {wallet && (wallet.verified
            ? <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200"><BadgeCheck className="w-3 h-3" />{t.verifiedBadge}</span>
            : <span data-testid="unverified-badge" className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/70"><ShieldAlert className="w-3 h-3" />{t.unverifiedBadge}</span>)}
          {testMode && <span data-testid="test-mode-badge" className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200"><FlaskConical className="w-3 h-3" />{t.testMode}</span>}
        </span>
        <button
          data-testid="toggle-balance-visibility"
          onClick={() => setHidden((h) => !h)}
          className="w-8 h-8 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>

      <div className="relative mt-3 flex items-end gap-2">
        <span data-testid="balance-amount-display" className="text-4xl sm:text-5xl font-black tabular-nums tracking-tight">
          {!wallet ? <span className="inline-block w-40 h-10 rounded-xl bg-white/10 animate-pulse align-middle" /> : hidden ? "••••••" : formatSAR(wallet.balance, lang)}
        </span>
        <span className="text-emerald-300 font-bold text-lg mb-1.5">{t.currency}</span>
      </div>

      <div className="relative mt-5 flex items-center justify-between">
        <button
          data-testid="copy-wallet-number"
          onClick={copyWallet}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          <span className="font-mono tracking-wider">{wallet?.wallet_number}</span>
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          data-testid="action-add-money"
          onClick={onAddMoney}
          className="flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 active:scale-95 transition-all"
        >
          <Plus className="w-4 h-4" /> {t.add}
        </button>
      </div>
    </motion.div>
  );
}
